import { logger } from '@loc/helpers/src/logger/client'
import { difference } from 'lodash-es'
import { match } from 'ts-pattern'

import {
  getLocalCacheData,
  removeLocalCacheData,
  setLocalCacheData,
} from './helpers/cache'

import type { KeyOptions, LocalCacheData } from './helpers/cache'
import type { StateCreator } from 'zustand'

import { IS_DEV } from '@/helpers/env'

export interface CacheDefault {
  /** 默认类型，如果关闭后刷新页面还是会弹出 */
  type: 'default'
}

export interface CacheCount {
  /** 计数类型，关闭指定次数后不再弹出 */
  type: 'count'
  count: number
}

export interface CacheInterval {
  /** 周期性类型，关闭后每多少天后弹出 */
  type: 'interval'
  /** 间隔天数 */
  timeout: number
  /** 弹出次数上限，如果超出该上限后不再弹出 */
  upperLimit?: number
}

export interface CacheUtil {
  /** 直到下线日期类型，给定下线日期以及每天需要弹几次，默认一次 */
  type: 'util'
  /** 指定下线时间戳，以秒计 */
  unixTimestamp: number
  /** 每天的限制，默认为 1，如果特殊场景下需要每次用户进入页面都弹出，可将该值设置为 null */
  dailyLimit?: number
}

export type CacheWithData = CacheCount | CacheInterval | CacheUtil
export type Cache = CacheDefault | CacheWithData

export interface PromotionCoreConfig {
  /** 默认为 false */
  open?: boolean
  priority: number
  component: React.ComponentType<any>
  /** 默认类型为 default */
  cache?: Cache
  /** 缓存后缀，用于区分同一分情况缓存，比如根据用户状态不同来缓存 */
  cacheKeySuffix?: string
  /** 是否下架 */
  disabled?: boolean
}

export type PromotionsCoreRegistry = Record<string, PromotionCoreConfig>

// ref: https://stackoverflow.com/a/72150495/8335317
export type PromotionKey<T extends PromotionsCoreRegistry> = Extract<
  keyof T,
  string
>

export interface InitOptions<T extends PromotionsCoreRegistry>
  extends Pick<PromotionCoreConfig, 'cache'> {
  key: PromotionKey<T>
  open: boolean
  cacheKeySuffix?: string
}

export const persistInMemoryCoreKeys = [
  'closed',
  'openKey',
  'initKeys',
] as const

export interface SitePromotionCoreState<T extends PromotionsCoreRegistry> {
  closed: boolean
  openKey: PromotionKey<T> | null
  initKeys: PromotionKey<T>[]

  promotions: T
}

export interface SitePromotionCoreActions<T extends PromotionsCoreRegistry> {
  init: (options: InitOptions<T>) => void
  closeAndTerminate: (key?: PromotionKey<T> | null) => void

  _setOpen: (key: PromotionKey<T>, open: boolean) => void
  _onClose: (options?: {
    /** 默认关闭当前 openKey */
    key?: PromotionKey<T> | null
    terminated?: boolean
  }) => void
  _addInitKey: (key: PromotionKey<T>) => void
  getTerminatedState: (key: PromotionKey<T>, cacheKeySuffix?: string) => boolean
}

export interface SitePromotionCoreSlice<T extends PromotionsCoreRegistry>
  extends SitePromotionCoreState<T>,
    SitePromotionCoreActions<T> {}

export interface CreateSitePromotionCoreSliceFactoryOptions {
  /** 缓存前缀，避免多类型复用缓存名称冲突 */
  cachePrefix: string
  onInit?: () => void
  onClose?: () => void
}

export function createSitePromotionCoreSliceFactory<
  T extends PromotionsCoreRegistry,
  S extends Record<string, any>,
>(
  initialState: SitePromotionCoreState<T>,
  options: CreateSitePromotionCoreSliceFactoryOptions,
) {
  const { cachePrefix, onInit, onClose } = options

  const createSitePromotionCoreSlice: StateCreator<
    SitePromotionCoreSlice<T> & S,
    [],
    [],
    SitePromotionCoreSlice<T>
  > = (...a) => {
    const [set, get] = a

    return {
      ...initialState,

      init: async (options) => {
        if (get().initKeys.includes(options.key)) {
          return
        }
        const dayjs = (await import('dayjs')).default
        const { key, open, cache, cacheKeySuffix } = options

        const keyOptions = {
          keyPrefix: cachePrefix,
          keySuffix: cacheKeySuffix,
        }

        const promotion = get().promotions[key]
        if (!promotion) {
          return
        }
        const localCacheData = getLocalCacheData(key, keyOptions)
        const mergedCache = cache || promotion.cache || { type: 'default' }

        if (localCacheData && localCacheData.type !== mergedCache.type) {
          removeLocalCacheData(key, keyOptions)
        }

        const latestLocalCacheData = getLocalCacheData(key, keyOptions)
        // 在localstorage中记录值后代表已经满足过打开条件
        if (latestLocalCacheData?.stat.terminated !== true) {
          match(mergedCache)
            .with({ type: 'count' }, (cache) => {
              const cacheData = getLocalCacheData(key, keyOptions)
              if (cacheData === null) {
                get()._setOpen(key, open)
              } else {
                if (cacheData.stat.closedCount >= cache.count) {
                  get()._setOpen(key, false)
                } else {
                  get()._setOpen(key, true)
                }
              }
            })
            .with({ type: 'interval' }, (cache) => {
              const cacheData = getLocalCacheData(key, keyOptions)
              if (cacheData === null) {
                get()._setOpen(key, open)
              } else {
                if (
                  cache.upperLimit &&
                  cacheData.stat.closedCount >= cache.upperLimit
                ) {
                  get()._setOpen(key, false)
                } else if (
                  dayjs().isAfter(
                    dayjs(cacheData.stat.unixTimestamp || 0)
                      .startOf('day')
                      .add(cache.timeout, 'day'),
                  )
                ) {
                  get()._setOpen(key, true)
                } else {
                  get()._setOpen(key, false)
                }
              }
            })
            .with({ type: 'util' }, (cache) => {
              const cacheData = getLocalCacheData(key, keyOptions)
              const closedCount = cacheData?.stat.closedCount || 0
              const cacheUnixTimestamp = cacheData?.stat.unixTimestamp
              if (cacheData === null) {
                get()._setOpen(key, open)
              } else {
                // 超时直接下线
                if (dayjs().isAfter(dayjs.unix(cache.unixTimestamp || 0))) {
                  get()._setOpen(key, false)
                } else if (
                  // 未超时，但是超过了每日限制
                  cacheUnixTimestamp &&
                  dayjs().isBefore(
                    dayjs.unix(cacheUnixTimestamp).endOf('day'),
                  ) &&
                  cache.dailyLimit !== null &&
                  closedCount >= (cache.dailyLimit || 1)
                ) {
                  get()._setOpen(key, false)
                } else {
                  get()._setOpen(key, true)

                  // 如果是新的一天，重置计数
                  if (
                    cacheUnixTimestamp &&
                    dayjs().isAfter(dayjs.unix(cacheUnixTimestamp).endOf('day'))
                  ) {
                    setLocalCacheData({
                      key,
                      data: {
                        ...cache,
                        stat: {
                          unixTimestamp: dayjs().unix(),
                          closedCount: 0,
                        },
                      },
                      keyOptions,
                    })
                  }
                }
              }
            })
            .otherwise(() => {
              get()._setOpen(key, open)
            })
        }

        get()._addInitKey(key)

        if (IS_DEV) {
          logger.debug(
            `not init ${cachePrefix} key`,
            difference(Object.keys(get().promotions), get().initKeys),
          )
        }

        set((s) => {
          return {
            ...s,
            promotions: {
              ...s.promotions,
              [key]: {
                ...s.promotions[key],
                cache: mergedCache,
                cacheKeySuffix,
              },
            },
          }
        })
        onInit?.()
      },
      closeAndTerminate: async (key) => {
        get()._onClose({
          key,
          terminated: true,
        })
        if (key && !get().initKeys.includes(key)) {
          get()._addInitKey(key)
        }
      },
      _setOpen: (key, open) => {
        set((s) => {
          return {
            ...s,
            promotions: {
              ...s.promotions,
              [key]: { ...s.promotions[key], open },
            },
          }
        })
      },
      _addInitKey: (key) => {
        if (get().initKeys.includes(key)) {
          return
        }
        set((s) => ({ ...s, initKeys: [...get().initKeys, key] }))
      },
      _onClose: async (options) => {
        const openKey = get().openKey
        const { key = openKey, terminated = false } = options || {}
        if (!key) {
          return
        }
        const dayjs = (await import('dayjs')).default

        const banner = get().promotions[key]
        const cache = banner.cache
        const cacheKeySuffix = banner.cacheKeySuffix

        const keyOptions = {
          keyPrefix: cachePrefix,
          keySuffix: cacheKeySuffix,
        }
        const cacheData = getLocalCacheData(key, keyOptions)
        const closedCount = (cacheData?.stat.closedCount || 0) + 1
        const stat: LocalCacheData['stat'] = {
          closedCount,
          unixTimestamp: dayjs().unix(),
        }

        if (terminated) {
          stat.terminated = true
        }

        match(cache).with(
          { type: 'count' },
          { type: 'interval' },
          { type: 'util' },
          (cache) => {
            setLocalCacheData({
              key,
              data: {
                ...cache,
                stat,
              },
              keyOptions,
            })
          },
        )

        set((s) => ({
          ...s,
          openKey: null,
          closed: true,
        }))
        onClose?.()
      },
      getTerminatedState: (key, cacheKeySuffix) => {
        const keyOptions = {
          keyPrefix: cachePrefix,
          keySuffix: cacheKeySuffix,
        } satisfies KeyOptions
        const localCacheData = getLocalCacheData(key, keyOptions)

        return localCacheData?.stat.terminated ?? false
      },
    }
  }

  return createSitePromotionCoreSlice
}
